import {
  Action, Checkbox, CheckboxGroup, Form,
  FormRow, List,
  ListItem, Option, PopupHint, SegmentedButton,
  Select,
} from '@op/opds.opux';
import { NewDatePicker } from '@op/opds.opux-new-date-picker';
import { useFormValidationAlert } from '@op/luna.yaweb-common-js/FormValidationAlert';
import React from 'react';
import { Message, useMessageGetter } from '@messageformat/react';
import { IO_CHANGE_INSURANCE } from '../../constants/actionTypes';
import { DEFAULT_LOCALE } from '../../constants/common';
import { useInsuranceOffer } from '../../hooks';
import { buildElementId } from '../../utils';
import ValidationError from '../common/ValidationError/ValidationError';
import ModalForExtremeSports from './ModalForExtremeSports';
import { FormInput } from '../common/Input/FormInput';

const PersonQuestions = (insurance) => {
  const { state, validateInsurance } = useInsuranceOffer();
  const ibRef = React.createRef();
  const getMessage = useMessageGetter();
  const getDefaultMessage = useMessageGetter([], { locale: DEFAULT_LOCALE });
  const { errors, removeErrors } = useFormValidationAlert();
  const newErrors = { add: [], remove: [] };
  const index = state.selectedInsurances.findIndex(
    (element) => element.id === insurance.id
  );
  const personDetails = state.selectedInsurances[index].questions;
  const meAndAnotherAdult = ['Me', 'Another adult'];
  const accidentPerson = [
    'tapaturmat ja sairaudet',
    'kattava tapaturma',
    'tapaturmat',
  ];

  const makeInputData = (data, label, answer, errorLabel) => {
    const value = data || '';
    return {
      value,
      dataKey: `${insurance.id}-${label}`,
      question: getMessage(`labelText.${label}`),
      answer: answer ? getMessage(answer) : value,
      defaultQuestion: getDefaultMessage(`labelText.${label}`),
      defaultAnswer: answer ? getDefaultMessage(answer) : value,
      errorLabel: errorLabel
        ? `${insurance.caption} : ${getMessage(
            `labelText.${label}`
          )} - ${getMessage(errorLabel)}`
        : '',
      getMessage,
    };
  };

  const getValue = (answer) => {
    return (answer || { value: '' }).value;
  };

 const getCheckboxValue = (answer) => {
    const v = (answer || {}).value;
    return v === true || v === 'true';
  };

  const getDate = (answer) => {
    return (answer || { date: null }).date;
  };

  const getOptionTitle = (event, key) => {
    const { value, selectedIndex } = event.target;
    return value === 'choose'
      ? 'uiText.choose'
      : `option.${key}.${String(selectedIndex).padStart(3, '0')}`;
  };

  const handleClick = (event) => {
    event.preventDefault();
    return ibRef.current.open();
  }

  const cleanInputsByIds = (arrayList, errs, insId) => {
    return arrayList
      .map((item) => buildElementId(insId, item)) // Create full IDs
      .filter((fullId) => errs.some((err) => err.id === fullId)); // Filter existing ones
  };


  const changeInsuranceDueDateDetail = (data, questionId) => {
    const { date, inputValue, invalid } = data;
    if (date !== null) {
      personDetails.calculatedDueDate = {
        date,
        isInvalid: inputValue === '' || invalid,
        ...makeInputData(inputValue, 'calculatedDueDate'),
      };
      validateInsurance({
        insurance,
        type: IO_CHANGE_INSURANCE,
        questions: personDetails,
        questionId: buildElementId(insurance.id, questionId),
      });
    }
  };

  return (
    <Form spacing="default">
      <div>
        <FormRow
          dataKey={`${insurance.id}-person`}
          className={'RS-person-questions-row'}
          label={<Message id="labelText.person" />}
          labelFor={buildElementId(insurance.id, 'PERSON_TYPE')}
          required
        >
          <Select
            value={getValue(personDetails.personType)}
            aria-describedby='personTypeError'
            name={`${insurance.id}-personType`}
            id={buildElementId(insurance.id, 'PERSON_TYPE')}
            onChange={(e) => {
              newErrors.remove = errors
                .filter((record) => record.id.startsWith(insurance.id))
                .map((record) => record.id);
              removeErrors(...newErrors.remove);
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: {
                  personType: makeInputData(
                    e.target.value,
                    'person',
                    getOptionTitle(e, 'person'),
                    'error.fill.in.missing.information'
                  ),
                },
                questionId: e.target.id,
              });
            }}
          >
            <option value="choose" key="choose">
              {getMessage('uiText.choose')}
            </option>
            <option value="Me" key="Me">
              {getMessage('option.person.001')}
            </option>
            <option value="Another adult" key="Another adult">
              {getMessage('option.person.002')}
            </option>
            <option value="Child" key="Child">
              {getMessage('option.person.003')}
            </option>
            <option value="Unborn child or twins" key="Unborn child or twins">
              {getMessage('option.person.004')}
            </option>
          </Select>
          <ValidationError
            id="personTypeError"
            requireVisible={false}
            errorId={buildElementId(insurance.id, 'PERSON_TYPE')}
            errorMessageId="error.fill.in.missing.information"
          />
        </FormRow>
      </div>
      { /* Another adult questions */}
      {getValue(personDetails.personType) === 'Another adult' && (
        <div>
          <FormInput
            type="string"
            value={getValue(personDetails.firstNameAnotherAdult)}
            inputId={buildElementId(insurance.id,'PERSON_FIRSTNAMEANOTHERADULT')}
            onChange={(firstNameAnotherAdult , error) => {
              personDetails.firstNameAnotherAdult = makeInputData(
                firstNameAnotherAdult,
                'FirstName'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: personDetails,
                questionId: `${insurance.id}-FirstName`,
                error,
              });                
            }}
            label={getMessage('labelText.FirstName')}
            required
          />

          <FormInput
            type="string"
            value={getValue(personDetails.lastNameAnotherAdult)}
            inputId={buildElementId(insurance.id,'PERSON_LASTNAMEANOTHERADULT')}
            onChange={(lastNameAnotherAdult , error) => {
              personDetails.lastNameAnotherAdult = makeInputData(
                lastNameAnotherAdult,
                'LastName'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: personDetails,
                questionId: `${insurance.id}-LastName`,
                error,
              });                
            }}
            label={getMessage('labelText.LastName')}
            required
          />

          <FormInput
            type="ssn-finland"
            value={getValue(personDetails.securityIdAnotherAdult)}
            inputId={buildElementId(insurance.id,'PERSON_SECURITYANOTHERADULT')}
            onChange={(securityIdAnotherAdult , error) => {
              personDetails.securityIdAnotherAdult = makeInputData(
                securityIdAnotherAdult.toUpperCase(),
                'SecurityId'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: personDetails,
                questionId: `${insurance.id}-SecurityId`,
                error,
              });                
            }}
            label={getMessage('labelText.SecurityId')}
            required
          />

        </div>
      )}

      {/* Me & Another adult questions */}
      {meAndAnotherAdult.indexOf(getValue(personDetails.personType)) > -1 && (
        <div>
          {getValue(personDetails.personType) === 'Me' && ( <FormRow
            dataKey={`${insurance.id}-Life`}
            className={'RS-person-questions-row'}
            label={<Message id="labelText.Life" />}
            labelFor={buildElementId(insurance.id, 'PERSON_LIFE')}
            hint={
              <PopupHint placement="top" label={getMessage('labelText.LifeAria')}>
                <Message id="hintText.Life" />
              </PopupHint>
            }
          >
            <SegmentedButton
              value={getValue(personDetails.Life)}
              aria-describedby={`${buildElementId(insurance.id, 'PERSON_LIFE_HINT')} lifeError`}
              name={`${insurance.id}-Life`}
              onChange={(e) => {
                personDetails.Life = makeInputData(
                  e.target.value,
                  'Life',
                  `buttonText.${e.target.value.toLowerCase()}`
                );
                // reset checkboxes to false on Life change
                personDetails.AdditionalLifeInsurancesSeriousIllness = makeInputData(
                  'false',
                  'AdditionalLifeInsurancesSeriousIllness',
                  'option.false'
                );
                personDetails.AdditionalLifeInsurancesTemporaryDisability = makeInputData(
                  'false',
                  'AdditionalLifeInsurancesTemporaryDisability',
                  'option.false'
                );
                personDetails.AdditionalLifeInsurancesPermanentDisability = makeInputData(
                  'false',
                  'AdditionalLifeInsurancesPermanentDisability',
                  'option.false'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: personDetails,
                  questionId: buildElementId(insurance.id, 'PERSON_LIFE'),
                });
              }}
              id={buildElementId(insurance.id, 'PERSON_LIFE')}
            >
              <Option value="Yes" key="Yes" required>
                {getMessage('buttonText.yes')}
              </Option>
              <Option value="No" key="No">
                {getMessage('buttonText.no')}
              </Option>
            </SegmentedButton>
            <ValidationError
              id="lifeError"
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'PERSON_LIFE')}
              errorMessageId="error.fill.in.missing.information"
            />
             <span id={buildElementId(insurance.id, 'PERSON_LIFE_HINT')}><Message id="hintText.LifeSecond" /></span>
          </FormRow>)}
          {getValue(personDetails.personType) === 'Me' && getValue(personDetails.Life) === 'Yes' && ( <FormRow 
            className={'RS-person-questions-row'}
            label={<Message id="labelText.LifeAdditionalInsurances" />}
            hint={
                <PopupHint placement="top" label={getMessage('labelText.LifeAdditionalInsurancesAria')} >
                  <Message id="hintText.LifeAdditionalInsurances" />
                </PopupHint>
            }
          >
            <CheckboxGroup>
              <Checkbox
                dataKey={`${insurance.id}-AdditionalLifeInsurancesSeriousIllness`}
                name={`${insurance.id}-AdditionalLifeInsurancesSeriousIllness`}
                checked={getCheckboxValue(personDetails.AdditionalLifeInsurancesSeriousIllness)}
                onChange={(e, checked) => {
                  const next = checked ? 'true' : 'false';
                  const prev = personDetails.AdditionalLifeInsurancesSeriousIllness?.value;
                  if (prev === next) return; // no dispatch if no change

                  const questions = {
                    ...personDetails,
                    AdditionalLifeInsurancesSeriousIllness: makeInputData(
                      next,
                      'AdditionalLifeInsurancesSeriousIllness',
                      `option.${next}`
                    ),
                  };

                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions,
                    questionId: buildElementId(insurance.id, 'PERSON_LIFE_SERIOUS_ILLNESS'),
                  });
                }}
              ><Message id="labelText.AdditionalLifeInsurancesSeriousIllness"/><PopupHint
                label={getMessage('labelText.AdditionalLifeInsurancesSeriousIllnessAria')}
                style={{ margin: '4px' }}
              >
                <Message id="hintText.AdditionalLifeInsurancesSeriousIllness"/>
              </PopupHint></Checkbox>
              
              <Checkbox
                dataKey={`${insurance.id}-AdditionalLifeInsurancesTemporaryDisability`}
                name={`${insurance.id}-AdditionalLifeInsurancesTemporaryDisability`}
                checked={getCheckboxValue(personDetails.AdditionalLifeInsurancesTemporaryDisability)}
                onChange={(e, checked) => {
                  const next = checked ? 'true' : 'false';
                  const prev = personDetails.AdditionalLifeInsurancesTemporaryDisability?.value;
                  if (prev === next) return; // no dispatch if no change

                  const questions = {
                    ...personDetails,
                    AdditionalLifeInsurancesTemporaryDisability: makeInputData(
                      next,
                      'AdditionalLifeInsurancesTemporaryDisability',
                      `option.${next}`
                    ),
                  };

                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions,
                    questionId: buildElementId(insurance.id, 'PERSON_LIFE_TEMPORARY_DISABILITY'),
                  });
                }}
              ><Message id="labelText.AdditionalLifeInsurancesTemporaryDisability"/><PopupHint
                label={getMessage('labelText.AdditionalLifeInsurancesTemporaryDisabilityAria')}
                style={{ margin: '4px' }}
              >
                <Message id="hintText.AdditionalLifeInsurancesTemporaryDisability"/>
              </PopupHint></Checkbox>
              <Checkbox
                dataKey={`${insurance.id}-AdditionalLifeInsurancesPermanentDisability`}
                name={`${insurance.id}-AdditionalLifeInsurancesPermanentDisability`}
                checked={getCheckboxValue(personDetails.AdditionalLifeInsurancesPermanentDisability)}
                onChange={(e, checked) => {
                  const next = checked ? 'true' : 'false';
                  const prev = personDetails.AdditionalLifeInsurancesPermanentDisability?.value;
                  if (prev === next) return; // no dispatch if no change

                  const questions = {
                    ...personDetails,
                    AdditionalLifeInsurancesPermanentDisability: makeInputData(
                      next,
                      'AdditionalLifeInsurancesPermanentDisability',
                      `option.${next}`
                    ),
                  };

                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions,
                    questionId: buildElementId(insurance.id, 'PERSON_LIFE_PERMANENT_DISABILITY'),
                  });
                }}  
              ><Message id="labelText.AdditionalLifeInsurancesPermanentDisability"/><PopupHint
                label={getMessage('labelText.AdditionalLifeInsurancesPermanentDisabilityAria')}
                style={{ margin: '4px' }}
              >
                <Message id="hintText.AdditionalLifeInsurancesPermanentDisability"/>
              </PopupHint></Checkbox>
            </CheckboxGroup>
          </FormRow>)}
          {getValue(personDetails.personType) === 'Me' && ( <FormRow
            dataKey={`${insurance.id}-Health`}
            className={'RS-person-questions-row'}
            label={<Message id="labelText.Health" />}
            labelFor={buildElementId(insurance.id, 'PERSON_HEALTH')}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.Health" />
              </PopupHint>
            }
          >
            <SegmentedButton
              id={buildElementId(insurance.id, 'PERSON_HEALTH')}
              aria-describedby='healthError'          
              value={getValue(personDetails.health)}
              name={`${insurance.id}-health`}
              onChange={(e) => {
                // cleaning values after the input
                delete personDetails.whichSportsMe;
                delete personDetails.whichHighRiskSportsMe;
                delete personDetails.healthCoverage;
                delete personDetails.highRiskSportsMe;
                delete personDetails.competitiveSportsMe;
                delete personDetails.competitiveSportsAnotherAdult;
                delete personDetails.whichSportsAnotherAdult;
                delete personDetails.highRiskSportsAnotherAdult;
                delete personDetails.whichHighRiskSportsAnotherAdult;

                const clearInputs = ["PERSON_WHICHSPORTSME","PERSON_WHICHHIGHRISKSPORTSME","PERSON_HEALTHCOVERAGE","PERSON_COMPETITIVESPORTSME","PERSON_HIGHRISKSPORTSME", "PERSON_COMPETITIVESPORTSANOTHERADULT", "PERSON_WHICHSPORTSANOTHERADULT", "PERSON_HIGHRISKSPORTSANOTHERADULT","PERSON_WHICHHIGHRISKSPORTSANOTHERADULT"]
                newErrors.remove = [...newErrors.remove, ...cleanInputsByIds(clearInputs, errors, insurance.id)]
                removeErrors(...newErrors.remove)
                personDetails.health = makeInputData(
                  e.target.value,
                  'Health',
                  `buttonText.${e.target.value.toLowerCase()}`
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: personDetails,
                  questionId: buildElementId(insurance.id, 'PERSON_HEALTH'),
                });
              }}
            >
              <Option value="Yes" key="Yes" required>
                {getMessage('buttonText.yes')}
              </Option>
              <Option value="No" key="No">
                {getMessage('buttonText.no')}
              </Option>
            </SegmentedButton>

            <ValidationError
              id="healthError"
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'PERSON_HEALTH')}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>)}
          {((getValue(personDetails.personType) === 'Me' && getValue(personDetails.health) === 'Yes') || getValue(personDetails.personType) === 'Another adult') && (
              <FormRow
                className={'RS-person-questions-row'}
                dataKey={`${insurance.id}-HealthCoverage`}
                label={getValue(personDetails.personType) === 'Me' ? <Message id="labelText.HealthCoverage" /> : <Message id="labelText.insuranceScope" />}
                labelFor={buildElementId(insurance.id, 'PERSON_HEALTHCOVERAGE')}
                hint={
                  <PopupHint placement="top">
                    <List border="none" className="removeMargin" size="default">
                      <ListItem>
                        <Message id="hintText.HealthCoverageTitle" />
                      </ListItem>
                      <ListItem>
                        <Message id="hintText.HealthCoverageFirst" />
                      </ListItem>
                      <ListItem>
                        <Message id="hintText.HealthCoverageSecond" />
                      </ListItem>
                      <ListItem>
                        <Message id="hintText.HealthCoverageThird" />
                      </ListItem>
                    </List>
                  </PopupHint>
                }
                required
              >
                <Select
                  value={getValue(personDetails.healthCoverage)}
                  aria-describedby='healthCoverageError'
                  name={`${insurance.id}-healthCoverage`}
                  onChange={(e) => {
                    personDetails.healthCoverage = makeInputData(
                      e.target.value,
                      'HealthCoverage',
                      getOptionTitle(e, 'HealthCoverage'),
                      'error.fill.in.missing.information'
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: personDetails,
                      questionId: buildElementId(
                        insurance.id,
                        'PERSON_HEALTHCOVERAGE'
                      ),
                    });
                  }}
                  id={buildElementId(insurance.id, 'PERSON_HEALTHCOVERAGE')}
                >
                  <option value="choose" key="choose">
                    {getMessage('uiText.choose')}
                  </option>
                  <option
                    value="tapaturmat ja sairaudet"
                    key="Illness and injury"
                  >
                    {getMessage('option.HealthCoverage.001')}
                  </option>
                  <option
                    value="kattava tapaturma"
                    key="Comprehensive accident"
                  >
                    {getMessage('option.HealthCoverage.002')}
                  </option>
                  <option value="tapaturmat" key="Accident">
                    {getMessage('option.HealthCoverage.003')}
                  </option>
                </Select>

                <ValidationError
                  id="healthCoverageError"
                  requireVisible={false}
                  errorId={buildElementId(
                    insurance.id,
                    'PERSON_HEALTHCOVERAGE'
                  )}
                  errorMessageId="error.fill.in.missing.information"
                />
              </FormRow>
            )}
          {getValue(personDetails.personType) === 'Me' &&
            getValue(personDetails.health) === 'Yes' &&
            accidentPerson.indexOf(getValue(personDetails.healthCoverage)) >
              -1 && (
              <FormRow
                className={'RS-person-questions-row'}
                dataKey={`${insurance.id}-competitiveSportsMe`}
                label={<Message id="labelText.competitiveSportsMe" />}
                labelFor={buildElementId(
                  insurance.id,
                  'PERSON_COMPETITIVESPORTSME'
                )}
                hint={
                  <PopupHint placement="top">
                    <Message id="hintText.competitiveSports" />
                  </PopupHint>
                }
              >
                <SegmentedButton
                  value={getValue(personDetails.competitiveSportsMe)}
                  aria-describedby='competitiveSportsMeError' 
                  name={`${insurance.id}-competitiveSportsMe`}
                  onChange={(e) => {
                    delete personDetails.whichSportsMe;
                    const clearInputs = ["PERSON_WHICHSPORTSME"]
                    newErrors.remove = [...newErrors.remove, ...cleanInputsByIds(clearInputs, errors, insurance.id)]
                    removeErrors(...newErrors.remove)

                    personDetails.competitiveSportsMe = makeInputData(
                      e.target.value,
                      'competitiveSportsMe',
                      `buttonText.${e.target.value.toLowerCase()}`
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: personDetails,
                      questionId: buildElementId(
                        insurance.id,
                        'PERSON_COMPETITIVESPORTSME'
                      ),
                    });
                  }}
                  id={buildElementId(
                    insurance.id,
                    'PERSON_COMPETITIVESPORTSME'
                  )}
                >
                  <Option value="Yes" key="Yes" required>
                    {getMessage('buttonText.yes')}
                  </Option>
                  <Option value="No" key="No">
                    {getMessage('buttonText.no')}
                  </Option>
                </SegmentedButton>
                <ValidationError
                  id="competitiveSportsMeError"
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'PERSON_COMPETITIVESPORTSME')}
                  errorMessageId="error.fill.in.missing.information"
                />
              </FormRow>
            )}
          {getValue(personDetails.personType) === 'Me' &&
            getValue(personDetails.health) === 'Yes' &&
            getValue(personDetails.competitiveSportsMe) === 'Yes' && (
              <FormInput
                type="sport"
                value={getValue(personDetails.whichSportsMe)}
                inputId={buildElementId(insurance.id, 'PERSON_WHICHSPORTSME')}
                onChange={(whichSportsMe , error) => {
                  personDetails.whichSportsMe = makeInputData(
                    whichSportsMe,
                    'whichSports'
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: personDetails,
                    questionId: `${insurance.id}-whichSports`,
                    error,
                  });                
                }}
                label={getMessage('labelText.whichSports')}
                required
              />             
            )}
        </div>
      )}
          {getValue(personDetails.personType) === 'Me' &&
            getValue(personDetails.health) === 'Yes' &&
            accidentPerson.indexOf(getValue(personDetails.healthCoverage)) >
              -1 && (
              <FormRow
                className={'RS-person-questions-row'}
                dataKey={`${insurance.id}-highRiskSportsMe`}
                label={
                  <div>
                    <Message id="labelText.highRiskSportsMe" />
                    <br />
                    <Action
                      tabIndex="0"
                      inline
                      href=''
                      onClick={handleClick}
                    >
                      {<Message id="labelText.highRiskSportsMeSecond" />}
                    </Action>
                    <ModalForExtremeSports ibRef={ibRef} />
                  </div>
                }
                labelFor={buildElementId(
                  insurance.id,
                  'PERSON_HIGHRISKSPORTSME'
                )}
              >
                <SegmentedButton
                  value={getValue(personDetails.highRiskSportsMe)}
                  name={`${insurance.id}-highRiskSportsMe`}
                  aria-describedby='highRiskSportsMeError' 
                  onChange={(e) => {
                    delete personDetails.whichHighRiskSportsMe;
                    const clearInputs = ["PERSON_WHICHHIGHRISKSPORTSME"]
                    newErrors.remove = [...newErrors.remove, ...cleanInputsByIds(clearInputs, errors, insurance.id)]
                    removeErrors(...newErrors.remove)

                    personDetails.highRiskSportsMe = makeInputData(
                      e.target.value,
                      'highRiskSportsMe',
                      `buttonText.${e.target.value.toLowerCase()}`
                    );
                    validateInsurance({
                      insurance,
                      type: IO_CHANGE_INSURANCE,
                      questions: personDetails,
                      questionId: buildElementId(
                        insurance.id,
                        'PERSON_HIGHRISKSPORTSME'
                      ),
                    });
                  }}
                  id={buildElementId(
                    insurance.id,
                    'PERSON_HIGHRISKSPORTSME'
                  )}
                >
                  <Option value="Yes" key="Yes" required>
                    {getMessage('buttonText.yes')}
                  </Option>
                  <Option value="No" key="No">
                    {getMessage('buttonText.no')}
                  </Option>
                </SegmentedButton>
                <ValidationError
                  id="highRiskSportsMeError"
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'PERSON_HIGHRISKSPORTSME')}
                  errorMessageId="error.fill.in.missing.information"
                />
              </FormRow>
            )}
          {getValue(personDetails.personType) === 'Me' &&
            getValue(personDetails.health) === 'Yes' &&
            getValue(personDetails.highRiskSportsMe) === 'Yes' && (
              <FormInput
                type="sport"
                value={getValue(personDetails.whichHighRiskSportsMe)}
                inputId={buildElementId(insurance.id, 'PERSON_WHICHHIGHRISKSPORTSME')}
                onChange={(whichHighRiskSportsMe , error) => {
                  personDetails.whichHighRiskSportsMe = makeInputData(
                    whichHighRiskSportsMe,
                    'whichHighRiskSports'
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: personDetails,
                    questionId: `${insurance.id}-whichHighRiskSports`,
                    error,
                  });                
                }}
                label={getMessage('labelText.whichHighRiskSports')}
                required
              />             
            )}

      {/*  Another adult questions */}
      {getValue(personDetails.personType) === 'Another adult' && (
          <div>
            <FormRow
              className={'RS-person-questions-row'}
              dataKey={`${insurance.id}-competitiveSports`}
              aria-describedby='competitiveSportsAnotherAdultError' 
              label={<Message id="labelText.competitiveSportsMe" />}
              labelFor={buildElementId(
                insurance.id,
                'PERSON_COMPETITIVESPORTSANOTHERADULT'
              )}
              hint={
                <PopupHint placement="top">
                  <Message id="hintText.competitiveSports" />
                </PopupHint>
              }
            >
              <SegmentedButton
                value={getValue(personDetails.competitiveSportsAnotherAdult)}
                name={`${insurance.id}-competitiveSportsAnotherAdult`}
                
                onChange={(e) => {
                  delete personDetails.whichSportsAnotherAdult;
                  const clearInputs = ["PERSON_COMPETITIVESPORTSANOTHERADULT"]
                  newErrors.remove = [...newErrors.remove, ...cleanInputsByIds(clearInputs, errors, insurance.id)]
                  removeErrors(...newErrors.remove)

                  personDetails.competitiveSportsAnotherAdult = makeInputData(
                    e.target.value,
                    'competitiveSportsMe',
                    `buttonText.${e.target.value.toLowerCase()}`
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: personDetails,
                    questionId: buildElementId(
                      insurance.id,
                      'PERSON_COMPETITIVESPORTSANOTHERADULT'
                    ),
                  });
                }}
                id={buildElementId(
                  insurance.id,
                  'PERSON_COMPETITIVESPORTSANOTHERADULT'
                )}
              >
                <Option value="Yes" key="Yes" required>
                  {getMessage('buttonText.yes')}
                </Option>
                <Option value="No" key="No">
                  {getMessage('buttonText.no')}
                </Option>
              </SegmentedButton>
              <ValidationError
                  id="competitiveSportsAnotherAdultError"
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'PERSON_COMPETITIVESPORTSANOTHERADULT')}
                  errorMessageId="error.fill.in.missing.information"
                />
            </FormRow>
            {getValue(personDetails.competitiveSportsAnotherAdult) ===
              'Yes' && (
              <FormInput
                type="sport"
                value={getValue(personDetails.whichSportsAnotherAdult)}
                inputId={buildElementId(insurance.id,'PERSON_WHICHSPORTSANOTHERADULT')}
                onChange={(whichSportsAnotherAdult , error) => {
                  personDetails.whichSportsAnotherAdult = makeInputData(
                    whichSportsAnotherAdult,
                    'whichSports'
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: personDetails,
                    questionId: `${insurance.id}-whichSportsAnotherAdult`,
                    error,
                  });                
                }}
                label={getMessage('labelText.whichSports')}
                required
              />             
            )}
          </div>
        )}

        {getValue(personDetails.personType) === 'Another adult' && (
          <div>
            <FormRow
              className={'RS-person-questions-row'}
              dataKey={`${insurance.id}-highRiskSports`}
              label={
                <div>
                  <Message id="labelText.highRiskSportsMe" />
                  <br />
                  <Action
                    tabIndex="0"
                    inline
                    href=''
                    onClick={handleClick}
                  >
                    {<Message id="labelText.highRiskSportsMeSecond" />}
                  </Action>
                  <ModalForExtremeSports ibRef={ibRef} />
                </div>
              }
              labelFor={buildElementId(
                insurance.id,
                'PERSON_HIGHRISKSPORTSANOTHERADULT'
              )}
            >
              <SegmentedButton
                value={getValue(personDetails.highRiskSportsAnotherAdult)}
                name={`${insurance.id}-highRiskSportsAnotherAdult`}
                aria-describedby='highRiskSportsAnotherAdultError' 
                onChange={(e) => {
                  delete personDetails.whichHighRiskSportsAnotherAdult;
                  const clearInputs = ["PERSON_WHICHHIGHRISKSPORTSANOTHERADULT"]
                  newErrors.remove = [...newErrors.remove, ...cleanInputsByIds(clearInputs, errors, insurance.id)]
                  removeErrors(...newErrors.remove)

                  personDetails.highRiskSportsAnotherAdult = makeInputData(
                    e.target.value,
                    'highRiskSports',
                    `buttonText.${e.target.value.toLowerCase()}`
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: personDetails,
                    questionId: buildElementId(
                      insurance.id,
                      'PERSON_HIGHRISKSPORTSANOTHERADULT'
                    ),
                  });
                }}
                id={buildElementId(
                  insurance.id,
                  'PERSON_HIGHRISKSPORTSANOTHERADULT'
                )}
              >
                <Option value="Yes" key="Yes" required>
                  {getMessage('buttonText.yes')}
                </Option>
                <Option value="No" key="No">
                  {getMessage('buttonText.no')}
                </Option>
              </SegmentedButton>
              <ValidationError
                  id="highRiskSportsAnotherAdultError"
                  requireVisible={false}
                  errorId={buildElementId(insurance.id, 'PERSON_HIGHRISKSPORTSANOTHERADULT')}
                  errorMessageId="error.fill.in.missing.information"
                />
            </FormRow>
            {getValue(personDetails.highRiskSportsAnotherAdult) ===
              'Yes' && (
              <FormInput
                type="sport"
                value={getValue(personDetails.whichHighRiskSportsAnotherAdult)}
                inputId={buildElementId(insurance.id,'PERSON_WHICHHIGHRISKSPORTSANOTHERADULT')}
                onChange={(whichHighRiskSportsAnotherAdult , error) => {
                  personDetails.whichHighRiskSportsAnotherAdult = makeInputData(
                    whichHighRiskSportsAnotherAdult,
                    'whichHighRiskSports'
                  );
                  validateInsurance({
                    insurance,
                    type: IO_CHANGE_INSURANCE,
                    questions: personDetails,
                    questionId: `${insurance.id}-whichHighRiskSportsAnotherAdult`,
                    error,
                  });                
                }}
                label={getMessage('labelText.whichHighRiskSports')}
                required
              />             
            )}
          </div>
        )}
        

      {/* Child questions */}
      {getValue(personDetails.personType) === 'Child' && (
        <div>
          <FormInput
            type="string"
            value={getValue(personDetails.firstNameChild)}
            inputId={buildElementId(insurance.id,'PERSON_FIRSTNAMECHILD')}
            onChange={(firstNameChild , error) => {
              personDetails.firstNameChild = makeInputData(
                firstNameChild,
                'FirstName',
                '',
                `${insurance.caption} : ${getMessage(
                  'labelText.FirstName'
                )} - ${error?.message}`
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: personDetails,
                questionId: `${insurance.id}-FirstName`,
                error,
              });                
            }}
            label={getMessage('labelText.FirstName')}
            required
          />

          <FormInput
            type="string"
            value={getValue(personDetails.lastNameChild)}
            inputId={buildElementId(insurance.id,'PERSON_LASTNAMECHILD')}
            onChange={(lastNameChild , error) => {
              personDetails.lastNameChild = makeInputData(
                lastNameChild,
                'LastName'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: personDetails,
                questionId: `${insurance.id}-LastName`,
                error,
              });                
            }}
            label={getMessage('labelText.LastName')}
            required
          />

          <FormInput
            type="ssn-finland"
            value={getValue(personDetails.securityIdChild)}
            inputId={buildElementId(insurance.id,'PERSON_SECURITYCHILD')}
            onChange={(securityIdChild , error) => {
              personDetails.securityIdChild = makeInputData(
                securityIdChild.toUpperCase(),
                'SecurityId'
              );
              validateInsurance({
                insurance,
                type: IO_CHANGE_INSURANCE,
                questions: personDetails,
                questionId: `${insurance.id}-SecurityId`,
                error,
              });                
            }}
            label={getMessage('labelText.SecurityId')}
            required
          />
                   
          <FormRow
            className={'RS-person-questions-row'}
            dataKey={`${insurance.id}-insuranceScope`}
            label={<Message id="labelText.insuranceScope" />}
            labelFor={buildElementId(
              insurance.id,
              'PERSON_INSURANCESCOPECHILD'
            )}
            hint={
              <PopupHint placement="top">
                <List border="none" className="removeMargin" size="default">
                  <ListItem>
                    <Message id="hintText.HealthCoverageTitle" />
                  </ListItem>
                  <ListItem>
                    <Message id="hintText.HealthCoverageFirst" />
                  </ListItem>
                  <ListItem>
                    <Message id="hintText.HealthCoverageSecond" />
                  </ListItem>
                  <ListItem>
                    <Message id="hintText.HealthCoverageThird" />
                  </ListItem>
                </List>
              </PopupHint>
            }
            required
          >
            <Select
              value={getValue(personDetails.insuranceScopeChild)}
              aria-describedby='insuranceScopeError'
              name={`${insurance.id}-insuranceScopeChild`}
              onChange={(e) => {
                personDetails.insuranceScopeChild = makeInputData(
                  e.target.value,
                  'insuranceScope',
                  getOptionTitle(e, 'insuranceScope')
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: personDetails,
                  questionId: e.target.id,
                });
              }}
              id={buildElementId(insurance.id, 'PERSON_INSURANCESCOPECHILD')}
            >
              <option value="choose" key="choose">
                {getMessage('uiText.choose')}
              </option>
              <option value="tapaturmat ja sairaudet" key="Illness and injury">
                {getMessage('option.insuranceScope.001')}
              </option>
              <option value="kattava tapaturma" key="Comprehensive accident">
                {getMessage('option.insuranceScope.002')}
              </option>
              <option value="tapaturmat" key="Accidenttest2">
                {getMessage('option.insuranceScope.003')}
              </option>
            </Select>
            <ValidationError
              id="insuranceScopeError"
              requireVisible={false}
              errorId={buildElementId(
                insurance.id,
                'PERSON_INSURANCESCOPECHILD'
              )}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
          <FormRow
            className={'RS-person-questions-row'}
            dataKey={`${insurance.id}-competitiveSports`}
            label={<Message id="labelText.competitiveSportsMe" />}
            labelFor={buildElementId(
              insurance.id,
              'PERSON_COMPETITIVESPORTSCHILD'
            )}
            hint={
              <PopupHint placement="top">
                <Message id="hintText.competitiveSports" />
              </PopupHint>
            }
          >
            <SegmentedButton
              value={getValue(personDetails.competitiveSportsChild)}
              name={`${insurance.id}-competitiveSportsChild`}
              aria-describedby='competitiveSportsChildError'
              onChange={(e) => {
                personDetails.competitiveSportsChild = makeInputData(
                  e.target.value,
                  'competitiveSportsMe',
                  `buttonText.${e.target.value.toLowerCase()}`
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: personDetails,
                  questionId: buildElementId(
                    insurance.id,
                    'PERSON_COMPETITIVESPORTSCHILD'
                  ),
                });
              }}
              id={buildElementId(insurance.id, 'PERSON_COMPETITIVESPORTSCHILD')}
            >
              <Option value="Yes" key="Yes" required>
                {getMessage('buttonText.yes')}
              </Option>
              <Option value="No" key="No">
                {getMessage('buttonText.no')}
              </Option>
            </SegmentedButton>
            <ValidationError
              id="competitiveSportsChildError"
              requireVisible={false}
              errorId={buildElementId(
                insurance.id,
                'PERSON_COMPETITIVESPORTSCHILD'
              )}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
          {getValue(personDetails.competitiveSportsChild) === 'Yes' && (
            <FormInput
              type="sport"
              value={getValue(personDetails.whichSportsChild)}
              inputId={buildElementId(insurance.id,'PERSON_WHICHSPORTSCHILD')}
              onChange={(whichSportsChild , error) => {
                personDetails.whichSportsChild = makeInputData(
                  whichSportsChild,
                  'whichSports'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: personDetails,
                  questionId: `${insurance.id}-whichSportsChild`,
                  error,
                });                
              }}
              label={getMessage('labelText.whichSports')}
              required
            />           
          )}
          <FormRow
            className={'RS-person-questions-row'}
            dataKey={`${insurance.id}-highRiskSportsChild`}
            label={
              <div>
                <Message id="labelText.highRiskSportsMe" />
                <br />
                <Action
                  tabIndex="0"
                  inline
                  href=''
                  onClick={handleClick}
                >
                  {<Message id="labelText.highRiskSportsMeSecond" />}
                </Action>
                <ModalForExtremeSports ibRef={ibRef} />
              </div>
            }
            labelFor={buildElementId(
              insurance.id,
              'PERSON_HIGHRISKSPORTSCHILD'
            )}
          >
            <SegmentedButton
              value={getValue(personDetails.highRiskSportsChild)}
              name={`${insurance.id}-highRiskSportsChild`}
              aria-describedby='highRiskSportsChildError'
              onChange={(e) => {
                personDetails.highRiskSportsChild = makeInputData(
                  e.target.value,
                  'highRiskSports',
                  `buttonText.${e.target.value.toLowerCase()}`
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: personDetails,
                  questionId: buildElementId(
                    insurance.id,
                    'PERSON_HIGHRISKSPORTSCHILD'
                  ),
                });
              }}
              id={buildElementId(insurance.id, 'PERSON_HIGHRISKSPORTSCHILD')}
            >
              <Option value="Yes" key="Yes" required>
                {getMessage('buttonText.yes')}
              </Option>
              <Option value="No" key="No">
                {getMessage('buttonText.no')}
              </Option>
            </SegmentedButton>
            <ValidationError
              id="highRiskSportsChildError"
              requireVisible={false}
              errorId={buildElementId(
                insurance.id,
                'PERSON_HIGHRISKSPORTSCHILD'
              )}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
          {getValue(personDetails.highRiskSportsChild) === 'Yes' && (
            <FormInput
              type="sport"
              value={getValue(personDetails.whichHighRiskSportsChild)}
              inputId={buildElementId(insurance.id,'PERSON_WHICHHIGHRISKSPORTSCHILD')}
              onChange={(whichHighRiskSportsChild , error) => {
                personDetails.whichHighRiskSportsChild = makeInputData(
                  whichHighRiskSportsChild,
                  'whichHighRiskSports'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: personDetails,
                  questionId: `${insurance.id}-whichHighRiskSportsChild`,
                  error,
                });                
              }}
              label={getMessage('labelText.whichHighRiskSports')}
              required
            />           
          )}
        </div>
      )}

      {/* Unborn child or twins questions */}
      {getValue(personDetails.personType) === 'Unborn child or twins' && (
        <div>
          <FormRow
            className={'RS-animal-questions-row'}
            dataKey={`${insurance.id}-calculatedDueDate`}
            label={<Message id="labelText.calculatedDueDate" />}
            labelFor={buildElementId(insurance.id, 'PERSON_CALCULATEDDUEDATE')}
            required={true}
          >
            <NewDatePicker
              dateInputProps={
                {
                  inputProps: {
                    id: buildElementId(insurance.id, 'PERSON_CALCULATEDDUEDATE'),
                  },
                }
              }
              allowPastDates={false}
              value={getDate(personDetails.calculatedDueDate)}
              name={`${insurance.id}-calculatedDueDate`}
              onChange={(date) => {
                changeInsuranceDueDateDetail(date, 'PERSON_CALCULATEDDUEDATE');
              }}
              onKeyDown={(e) => { e.key === 'Enter' && e.preventDefault() }}
            />
            <ValidationError
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'PERSON_CALCULATEDDUEDATE')}
              errorMessageId="errorText.personBirthDate"
              className={'RS-person-unborn-child-errortext'}
            />
          </FormRow>
          <FormRow
            className={'RS-person-questions-row'}
            dataKey={`${insurance.id}-personUnbornMotherChild`}
            label={<Message id="labelText.personUnbornMotherChild" />}
            labelFor={buildElementId(insurance.id, 'PERSON_Mother_Child')}
          >
            <SegmentedButton
              value={getValue(personDetails.motherChild)}
              aria-describedby='motherChildError'
              name={`${insurance.id}-motherChild`}
              onChange={(e) => {
                personDetails.motherChild = makeInputData(
                  e.target.value,
                  'personUnbornMotherChild',
                  `buttonText.${e.target.value.toLowerCase()}`
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: personDetails,
                  questionId: buildElementId(
                    insurance.id,
                    'PERSON_Mother_Child'
                  ),
                });
              }}
              id={buildElementId(insurance.id, 'PERSON_Mother_Child')}
            >
              <Option value="Yes" key="Yes" required>
                {getMessage('buttonText.yes')}
              </Option>
              <Option value="No" key="No">
                {getMessage('buttonText.no')}
              </Option>
            </SegmentedButton>
            <ValidationError
              id="motherChildError"
              requireVisible={false}
              errorId={buildElementId(insurance.id, 'PERSON_Mother_Child')}
              errorMessageId="error.fill.in.missing.information"
            />
          </FormRow>
          {getValue(personDetails.motherChild) === 'No' && (
            <FormInput
              type="ssn-finland"
              value={getValue(personDetails.mothersID)}
              inputId={buildElementId(insurance.id,'PERSON_MOTHERID')}
              onChange={(mothersID , error) => {
                personDetails.mothersID = makeInputData(
                  mothersID.toUpperCase(),
                  'mothersIdentificationNumber'
                );
                validateInsurance({
                  insurance,
                  type: IO_CHANGE_INSURANCE,
                  questions: personDetails,
                  questionId: `${insurance.id}-mothersIdentificationNumber`,
                  error,
                });                
              }}
              label={getMessage('labelText.mothersIdentificationNumber')}
              hint={
                <PopupHint placement="top">
                  <Message id="hintText.mothersIdentificationNumber" />
                </PopupHint>
              }
              required
            />        
          )}
        </div>
      )}
    </Form>
  );
};

export default PersonQuestions;
